import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { IPlan } from "~/types/billing";
import adCreative from "~/assets/onboarding/ad-creative.webp";
import dress from "~/assets/onboarding/dress.webp";
import jacket from "~/assets/onboarding/jacket.webp";
import tote from "~/assets/onboarding/tote.webp";
import watch from "~/assets/onboarding/watch.webp";
import styles from "./OnboardingFlow.module.css";
import {
  FEATURE_DETAILS,
  ONBOARDING_FEATURES,
  type OnboardingFeature,
  type TrialLimit,
  getTrialLimits,
  orderedFeatures,
  toggleFeature,
} from "./onboarding-utils";
import { OnboardingIcon, type OnboardingIconName } from "./OnboardingIcon";
import { useOnboardingTracking } from "./useOnboardingTracking";

const TOTAL_STEPS = 3;

type Step = "features" | "preview" | "trial" | "welcome";

type OnboardingFlowProps = {
  // Set after payment: the tools picked before checkout, for the welcome
  // screen's quick starts.
  welcomeFeatures: OnboardingFeature[] | null;
  // Active plans, for what the Starter trial includes.
  plans: IPlan[] | undefined;
  // "Unlock DropshipTool" or "Skip": open the existing checkout.
  onCheckout: (features: OnboardingFeature[]) => void;
  // The welcome screen is dismissed: show the app.
  onFinish: () => void;
};

export function OnboardingFlow({
  welcomeFeatures,
  plans,
  onCheckout,
  onFinish,
}: OnboardingFlowProps) {
  const track = useOnboardingTracking();
  const [step, setStep] = useState<Step>(
    welcomeFeatures ? "welcome" : "features"
  );
  const [features, setFeatures] = useState<OnboardingFeature[]>([]);

  useEffect(() => {
    track("onboarding_step_view", { step });
    // Once per step, not each time the tracker is recreated.
  }, [step]);

  useEffect(() => {
    if (welcomeFeatures) setStep("welcome");
  }, [welcomeFeatures]);

  const skip = () => {
    track("onboarding_skip", { step });
    onCheckout(features);
  };

  const unlock = () => {
    track("onboarding_unlock_click", { tools: features.join(",") });
    onCheckout(features);
  };

  return (
    <div className={styles.container}>
      {step === "features" && (
        <FeaturesStep
          features={features}
          onToggle={(feature) =>
            setFeatures((selected) => toggleFeature(selected, feature))
          }
          onNext={() => setStep("preview")}
          onSkip={skip}
        />
      )}
      {step === "preview" && (
        <PreviewStep
          features={orderedFeatures(features)}
          onBack={() => setStep("features")}
          onNext={() => setStep("trial")}
          onSkip={skip}
        />
      )}
      {step === "trial" && (
        <TrialStep
          features={features}
          plans={plans}
          onBack={() => setStep("preview")}
          onUnlock={unlock}
          onSkip={skip}
        />
      )}
      {step === "welcome" && (
        <WelcomeStep
          features={orderedFeatures(welcomeFeatures ?? [])}
          onDashboard={onFinish}
        />
      )}
    </div>
  );
}

function StepHeader({
  current,
  title,
  subtitle,
}: {
  current: number;
  title: string;
  subtitle: string;
}) {
  const { t } = useTranslation();
  return (
    <div className={styles.header}>
      <div className={styles.stepIndicator}>
        <span className={styles.stepLabel}>
          {String(t("onboarding_v2.step_of", { current, total: TOTAL_STEPS }))}
        </span>
        <div className={styles.stepBars} aria-hidden="true">
          {Array.from({ length: TOTAL_STEPS }, (_, index) => (
            <span
              key={index}
              className={`${styles.stepBar} ${index < current ? styles.stepBarActive : ""}`}
            />
          ))}
        </div>
      </div>
      <h2 className={styles.title}>{title}</h2>
      <p className={styles.subtitle}>{subtitle}</p>
    </div>
  );
}

function PrimaryButton({
  children,
  onClick,
  disabled,
  arrow,
}: {
  children: string;
  onClick: () => void;
  disabled?: boolean;
  arrow?: boolean;
}) {
  return (
    <button
      type="button"
      className={styles.primaryButton}
      onClick={onClick}
      disabled={disabled}>
      {children}
      {arrow && <OnboardingIcon name="arrow-right" />}
    </button>
  );
}

function TextButton({
  children,
  onClick,
  backArrow,
}: {
  children: string;
  onClick: () => void;
  backArrow?: boolean;
}) {
  return (
    <button type="button" className={styles.textButton} onClick={onClick}>
      {backArrow && <OnboardingIcon name="arrow-left" />}
      {children}
    </button>
  );
}

function Footer({
  onBack,
  onSkip,
  skipLabel,
  children,
}: {
  onBack?: () => void;
  onSkip: () => void;
  skipLabel: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className={styles.footer}>
      {onBack ? (
        <TextButton backArrow onClick={onBack}>
          {String(t("onboarding_v2.back"))}
        </TextButton>
      ) : (
        <TextButton onClick={onSkip}>{skipLabel}</TextButton>
      )}
      <div className={styles.footerEnd}>
        {onBack && <TextButton onClick={onSkip}>{skipLabel}</TextButton>}
        {children}
      </div>
    </div>
  );
}

function FeaturesStep({
  features,
  onToggle,
  onNext,
  onSkip,
}: {
  features: OnboardingFeature[];
  onToggle: (feature: OnboardingFeature) => void;
  onNext: () => void;
  onSkip: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className={styles.card}>
      <StepHeader
        current={1}
        title={String(t("onboarding_v2.features.title"))}
        subtitle={String(t("onboarding_v2.features.subtitle"))}
      />

      <div className={styles.tileGrid}>
        {ONBOARDING_FEATURES.map((feature) => {
          const selected = features.includes(feature);
          return (
            <button
              key={feature}
              type="button"
              aria-pressed={selected}
              className={`${styles.tile} ${styles.featureTile} ${selected ? styles.tileSelected : ""}`}
              onClick={() => onToggle(feature)}>
              <span className={styles.tileIcon}>
                <OnboardingIcon name={FEATURE_DETAILS[feature].icon} />
              </span>
              <span className={styles.tileText}>
                <span className={styles.featureName}>
                  {String(t(`onboarding_v2.features.${feature}.title`))}
                </span>
                <span className={styles.featureDescription}>
                  {String(t(`onboarding_v2.features.${feature}.description`))}
                </span>
                <span className={styles.featureTool}>
                  {String(t(`onboarding_v2.tools.${feature}`))}
                </span>
              </span>
              <span className={styles.checkbox} aria-hidden="true">
                {selected && <OnboardingIcon name="check" />}
              </span>
            </button>
          );
        })}
      </div>

      <div className={styles.divider} />

      <Footer onSkip={onSkip} skipLabel={String(t("onboarding_v2.skip_setup"))}>
        {features.length > 0 && (
          <span className={styles.selectedCount}>
            {String(
              t("onboarding_v2.features.selected", { count: features.length })
            )}
          </span>
        )}
        <PrimaryButton arrow disabled={features.length === 0} onClick={onNext}>
          {String(t("onboarding_v2.next"))}
        </PrimaryButton>
      </Footer>
    </div>
  );
}

function PreviewStep({
  features,
  onBack,
  onNext,
  onSkip,
}: {
  features: OnboardingFeature[];
  onBack: () => void;
  onNext: () => void;
  onSkip: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className={styles.card}>
      <StepHeader
        current={2}
        title={String(t("onboarding_v2.preview.title"))}
        subtitle={String(
          t("onboarding_v2.preview.subtitle", { count: features.length })
        )}
      />

      <div className={styles.previewGrid}>
        {features.map((feature) => {
          const Preview = PREVIEWS[feature];
          return <Preview key={feature} />;
        })}
      </div>

      <div className={styles.divider} />

      <Footer
        onBack={onBack}
        onSkip={onSkip}
        skipLabel={String(t("onboarding_v2.skip_for_now"))}>
        <PrimaryButton arrow onClick={onNext}>
          {String(t("onboarding_v2.next"))}
        </PrimaryButton>
      </Footer>
    </div>
  );
}

// ---------- previews, with the design's example data ----------

function PreviewCard({
  feature,
  icon,
  children,
}: {
  feature: OnboardingFeature;
  icon?: OnboardingIconName;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className={styles.previewCard}>
      <div className={styles.previewHeader}>
        <span className={styles.previewIcon}>
          <OnboardingIcon
            name={icon ?? FEATURE_DETAILS[feature].icon}
            size={16}
          />
        </span>
        <span className={styles.previewTitle}>
          {String(t(`onboarding_v2.preview.${feature}.title`))}
        </span>
        <span className={styles.pill}>
          {String(t(`onboarding_v2.tools.${feature}`))}
        </span>
      </div>
      {children}
    </div>
  );
}

function Bars({ heights }: { heights: number[] }) {
  return (
    <div className={styles.bars} aria-hidden="true">
      {heights.map((height, index) => (
        <span
          key={index}
          className={`${styles.bar} ${index === heights.length - 1 ? styles.barCurrent : ""}`}
          style={{ height }}
        />
      ))}
    </div>
  );
}

function MiniStats({
  stats,
}: {
  stats: { value: string; label: string; success?: boolean }[];
}) {
  return (
    <div className={styles.miniStats}>
      {stats.map((stat) => (
        <div key={stat.label} className={styles.miniStat}>
          <span
            className={`${styles.miniValue} ${stat.success ? styles.miniValueSuccess : ""}`}>
            {stat.value}
          </span>
          <span className={styles.miniLabel}>{stat.label}</span>
        </div>
      ))}
    </div>
  );
}

function StoreRow({
  domain,
  kind,
  badge,
}: {
  domain: string;
  kind: string;
  badge?: string;
}) {
  return (
    <div className={styles.previewRow}>
      <span className={styles.avatar} aria-hidden="true">
        {domain[0].toUpperCase()}
      </span>
      <span className={styles.rowText}>
        <span className={`${styles.rowTitle} ${styles.rowTitleStrong}`}>
          {domain}
        </span>
        <span className={styles.rowSub}>{kind}</span>
      </span>
      {badge && (
        <span className={`${styles.pill} ${styles.pillSuccess}`}>{badge}</span>
      )}
    </div>
  );
}

function ProductLibraryPreview() {
  const { t } = useTranslation();
  const products = [
    {
      image: tote,
      title: "Quilted Puffer Tote",
      revenue: "$18.4k",
      growth: "+182%",
    },
    {
      image: watch,
      title: "Minimalist Gold Mesh Watch",
      revenue: "$12.9k",
      growth: "+96%",
    },
    {
      image: jacket,
      title: "Utility Bomber Jacket",
      revenue: "$9.7k",
      growth: "+64%",
    },
  ];
  return (
    <PreviewCard feature="product_library">
      {products.map((product) => (
        <div key={product.title} className={styles.previewRow}>
          <img className={styles.rowImage} src={product.image} alt="" />
          <span className={styles.rowText}>
            <span className={styles.rowTitle}>{product.title}</span>
            <span className={styles.rowSub}>
              {String(
                t("onboarding_v2.preview.revenue_per_month", {
                  amount: product.revenue,
                })
              )}
            </span>
          </span>
          <span className={styles.growth}>{product.growth}</span>
        </div>
      ))}
      <span className={styles.note}>
        <OnboardingIcon name="chart" size={14} />
        {String(t("onboarding_v2.preview.product_library.note"))}
      </span>
    </PreviewCard>
  );
}

function AdSpyPreview() {
  const { t } = useTranslation();
  return (
    <PreviewCard feature="ad_spy">
      <div className={styles.adCreative}>
        <img src={adCreative} alt="" />
        <span className={styles.pill}>
          {String(t("onboarding_v2.preview.ad_spy.running", { count: 18 }))}
        </span>
      </div>
      <div className={styles.adStats}>
        <span className={styles.adStat}>
          <OnboardingIcon name="heart" />
          24.1k
        </span>
        <span className={styles.adStat}>
          <OnboardingIcon name="message" />
          1,208
        </span>
        <span className={styles.adStat}>
          <OnboardingIcon name="eye" />
          312k
        </span>
      </div>
      <p className={styles.quote}>
        “Our #1 highlighter is back in stock — get the glow everyone’s talking
        about.”
      </p>
    </PreviewCard>
  );
}

function StoreTrackerPreview() {
  const { t } = useTranslation();
  return (
    <PreviewCard feature="store_tracker">
      <StoreRow
        domain="glowhaus.co"
        kind={String(t("onboarding_v2.preview.store_kind", { kind: "Beauty" }))}
        badge={String(t("onboarding_v2.preview.store_tracker.tracking"))}
      />
      <span className={styles.rowText}>
        <span className={styles.bigValue}>$48.2k</span>
        <span className={styles.rowSub}>
          {String(t("onboarding_v2.preview.revenue_last_30_days"))}
        </span>
      </span>
      <Bars
        heights={[18, 24, 20, 30, 26, 34, 28, 38, 32, 40, 36, 44, 42, 52]}
      />
      <span className={`${styles.note} ${styles.noteSuccess}`}>
        <OnboardingIcon name="package" />
        {String(
          t("onboarding_v2.preview.store_tracker.new_products", { count: 12 })
        )}
      </span>
    </PreviewCard>
  );
}

function CompetitorAnalysisPreview() {
  const { t } = useTranslation();
  const bestSellers = [
    { image: dress, title: "Girls’ Ruffle Summer Dress", orders: 410 },
    { image: tote, title: "Quilted Puffer Tote", orders: 290 },
  ];
  return (
    <PreviewCard feature="competitor_analysis">
      <StoreRow
        domain="lunaloom.com"
        kind={String(
          t("onboarding_v2.preview.store_kind", { kind: "Fashion" })
        )}
      />
      <MiniStats
        stats={[
          { value: "148", label: String(t("onboarding_v2.preview.products")) },
          { value: "$34", label: String(t("onboarding_v2.preview.avg_price")) },
          {
            value: "$62k",
            label: String(t("onboarding_v2.preview.revenue_short")),
          },
        ]}
      />
      <span className={styles.sectionHeading}>
        {String(t("onboarding_v2.preview.competitor_analysis.best_sellers"))}
      </span>
      {bestSellers.map((product) => (
        <div key={product.title} className={styles.previewRow}>
          <img
            className={`${styles.rowImage} ${styles.rowImageSmall}`}
            src={product.image}
            alt=""
          />
          <span className={styles.rowTitle}>{product.title}</span>
          <span className={styles.rowSub}>
            {String(
              t("onboarding_v2.preview.orders_per_month", {
                count: product.orders,
              })
            )}
          </span>
        </div>
      ))}
    </PreviewCard>
  );
}

function SalesInsightsPreview() {
  const { t } = useTranslation();
  return (
    <PreviewCard feature="sales_insights">
      <div className={styles.previewRow}>
        <img className={styles.rowImage} src={watch} alt="" />
        <span className={styles.rowText}>
          <span className={styles.rowTitle}>Minimalist Gold Mesh Watch</span>
          <span className={styles.rowSub}>
            {String(
              t("onboarding_v2.preview.sales_insights.sold_by", { count: 14 })
            )}
          </span>
        </span>
      </div>
      <span className={styles.rowText}>
        <span className={styles.bigValue}>
          {String(t("onboarding_v2.preview.per_day", { amount: "$1,240" }))}
        </span>
        <span className={styles.rowSub}>
          {String(t("onboarding_v2.preview.revenue_last_30_days"))}
        </span>
      </span>
      <Bars
        heights={[
          20, 22, 19, 26, 24, 28, 25, 31, 29, 33, 30, 36, 35, 40, 38, 44,
        ]}
      />
      <MiniStats
        stats={[
          {
            value: "33",
            label: String(
              t("onboarding_v2.preview.sales_insights.orders_per_day")
            ),
          },
          {
            value: "$37.80",
            label: String(t("onboarding_v2.preview.avg_price")),
          },
          {
            value: "+24%",
            label: String(t("onboarding_v2.preview.sales_insights.trend")),
            success: true,
          },
        ]}
      />
    </PreviewCard>
  );
}

function ThemeDetectorPreview() {
  const { t } = useTranslation();
  return (
    <PreviewCard feature="theme_detector">
      <div className={styles.urlField}>
        <OnboardingIcon name="link" />
        <span>glowhaus.co</span>
        <OnboardingIcon name="search" />
      </div>
      <div className={styles.themePreview} aria-hidden="true">
        <span className={styles.themeNav} />
        <div className={styles.themeHero}>
          <img src={adCreative} alt="" />
          <span className={styles.themeLines}>
            <span
              className={`${styles.themeLine} ${styles.themeLineDark}`}
              style={{ width: 70 }}
            />
            <span className={styles.themeLine} style={{ width: 90 }} />
            <span className={styles.themeLine} style={{ width: 60 }} />
            <span className={styles.themeButton} />
          </span>
        </div>
      </div>
      <div className={styles.result}>
        <span className={styles.rowText}>
          <span className={`${styles.rowTitle} ${styles.rowTitleStrong}`}>
            {String(
              t("onboarding_v2.preview.theme_detector.theme", { name: "Dawn" })
            )}
          </span>
          <span className={styles.rowSub}>
            {String(t("onboarding_v2.preview.theme_detector.free_theme"))}
          </span>
        </span>
        <span className={`${styles.pill} ${styles.pillSuccess}`}>
          {String(t("onboarding_v2.preview.theme_detector.detected"))}
        </span>
      </div>
    </PreviewCard>
  );
}

const PREVIEWS: Record<OnboardingFeature, () => JSX.Element> = {
  product_library: ProductLibraryPreview,
  ad_spy: AdSpyPreview,
  store_tracker: StoreTrackerPreview,
  competitor_analysis: CompetitorAnalysisPreview,
  sales_insights: SalesInsightsPreview,
  theme_detector: ThemeDetectorPreview,
};

// ---------- what the trial includes ----------

function LimitValue({ limit }: { limit: TrialLimit }) {
  const { t } = useTranslation();
  const value = limit.value.toLocaleString("en-US");
  return (
    <span className={styles.limitValue}>
      {limit.period
        ? String(t(`onboarding_v2.trial.per_${limit.period}`, { value }))
        : value}
    </span>
  );
}

function TrialStep({
  features,
  plans,
  onBack,
  onUnlock,
  onSkip,
}: {
  features: OnboardingFeature[];
  plans: IPlan[] | undefined;
  onBack: () => void;
  onUnlock: () => void;
  onSkip: () => void;
}) {
  const { t } = useTranslation();
  const limits = useMemo(() => getTrialLimits(plans), [plans]);

  return (
    <div className={styles.card}>
      <StepHeader
        current={3}
        title={String(t("onboarding_v2.trial.title"))}
        subtitle={String(t("onboarding_v2.trial.subtitle"))}
      />

      <div className={styles.limitGrid}>
        {limits.map((limit) => {
          const picked = !!limit.feature && features.includes(limit.feature);
          return (
            <div key={limit.key} className={styles.limitCard}>
              <span
                className={`${styles.limitIcon} ${picked ? styles.limitIconPicked : ""}`}>
                <OnboardingIcon name={limit.icon} size={20} />
              </span>
              <span className={styles.rowText}>
                <LimitValue limit={limit} />
                <span className={styles.limitLabel}>
                  {String(t(`onboarding_v2.trial.limits.${limit.key}`))}
                </span>
              </span>
              {picked && (
                <span className={styles.pill}>
                  {String(t("onboarding_v2.trial.your_pick"))}
                </span>
              )}
            </div>
          );
        })}
      </div>

      <p className={styles.planNote}>
        {String(t("onboarding_v2.trial.plan_note"))}
      </p>

      <div className={styles.divider} />

      <Footer
        onBack={onBack}
        onSkip={onSkip}
        skipLabel={String(t("onboarding_v2.skip_for_now"))}>
        <PrimaryButton arrow onClick={onUnlock}>
          {String(t("onboarding_v2.trial.unlock"))}
        </PrimaryButton>
      </Footer>
    </div>
  );
}

// ---------- after payment: welcome and quick starts ----------

function WelcomeStep({
  features,
  onDashboard,
}: {
  features: OnboardingFeature[];
  onDashboard: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className={`${styles.card} ${styles.success}`}>
      <div className={`${styles.successIcon} ${styles.welcomeIcon}`}>
        <OnboardingIcon name="check" size={22} />
      </div>
      <div className={styles.successText}>
        <h2 className={styles.title}>
          {String(t("onboarding_v2.welcome.title"))}
        </h2>
        <p className={styles.successSubtitle}>
          {String(
            t("onboarding_v2.welcome.subtitle", { count: features.length })
          )}
        </p>
      </div>

      <div className={styles.quickGrid}>
        {features.map((feature) => (
          <div key={feature} className={styles.quickCard}>
            <span className={styles.previewIcon}>
              <OnboardingIcon name={FEATURE_DETAILS[feature].icon} size={16} />
            </span>
            <span className={styles.quickTitle}>
              {String(t(`onboarding_v2.welcome.${feature}.title`))}
            </span>
            <p className={styles.quickText}>
              {String(t(`onboarding_v2.welcome.${feature}.description`))}
            </p>
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => {
                onDashboard();
                navigate(FEATURE_DETAILS[feature].path);
              }}>
              {String(
                t("onboarding_v2.welcome.open", {
                  tool: String(t(`onboarding_v2.tools.${feature}`)),
                })
              )}
              <OnboardingIcon name="arrow-right" size={12} />
            </button>
          </div>
        ))}
      </div>

      <div className={styles.divider} />

      <div className={`${styles.footer} ${styles.footerEndOnly}`}>
        <PrimaryButton arrow onClick={onDashboard}>
          {String(t("onboarding_v2.welcome.go_to_dashboard"))}
        </PrimaryButton>
      </div>
    </div>
  );
}
